import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, User, BookOpen, FileText, Sparkles, BadgeCheck, Pause, Play } from "lucide-react";
import { CONTACT } from "../../config/contact";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_accessibility-first-5/artifacts/4w169tlv_ChatGPT%20Image%20Jan%201%2C%202026%2C%2002_13_50%20AM.png";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.55, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

const FORMATS = [
  { label: "HTML5", color: "#B93A17", top: "8%", right: "14%" },
  { label: "PDF", color: "#B30B00", top: "26%", right: "4%" },
  { label: "XML", color: "#6D28D9", top: "44%", right: "12%" },
  { label: "EPUB", color: "#00776E", top: "62%", right: "3%" },
  { label: "WORD", color: "#185ABD", top: "80%", right: "13%" },
  { label: "CSS", color: "#10629E", top: "17%", right: "27%" },
  { label: "A11Y", color: "#0B1F3A", top: "53%", right: "26%" },
  { label: "JAVA", color: "#B45309", top: "89%", right: "27%" },
  { label: "MOBI", color: "#A16207", top: "2%", right: "26%" },
  { label: "InDesign", color: "#BE123C", top: "35%", right: "2%" },
  { label: "Braille", color: "#0F766E", top: "71%", right: "24%" },
  { label: "Photoshop", color: "#0E63C4", top: "14%", right: "3%" },
  { label: "JPEG", color: "#7C3AED", top: "95%", right: "8%" },
  { label: "PNG", color: "#047857", top: "38%", right: "22%" },
  { label: "BMP", color: "#B91C1C", top: "58%", right: "15%" },
  { label: "TIFF", color: "#6D28D9", top: "83%", right: "2%" },
  { label: "SVG", color: "#92600A", top: "23%", right: "16%" },
].map((f, i) => ({ ...f, delay: i * 0.5 }));

const CHIP_CYCLE = 17 * 0.5; // one chip launches every 0.5s, full loop = 8.5s

function FormatChip({ label, color, top, right, delay, paused }) {
  if (paused) {
    return (
      <div
        aria-hidden
        className="absolute z-10 flex items-center gap-1.5 rounded-full border bg-white px-3 py-1.5 shadow-[0_10px_30px_-12px_rgba(11,31,58,0.35)]"
        style={{ borderColor: `${color}40`, left: `${100 - parseFloat(right)}%`, top, transform: "translate(-50%, -50%)" }}
      >
        <FileText className="h-3.5 w-3.5" style={{ color }} strokeWidth={2.4} />
        <span className="text-[11px] sm:text-xs font-bold tracking-wide" style={{ color }}>
          {label}
        </span>
      </div>
    );
  }
  return (
    <motion.div
      aria-hidden
      className="absolute z-10 flex items-center gap-1.5 rounded-full border bg-white px-3 py-1.5 shadow-[0_10px_30px_-12px_rgba(11,31,58,0.35)]"
      style={{ borderColor: `${color}40` }}
      initial={{ left: "50%", top: "50%", opacity: 0, scale: 0.3 }}
      animate={{
        left: ["50%", `${100 - parseFloat(right)}%`],
        top: ["50%", top],
        opacity: [0, 1, 1, 0],
        scale: [0.3, 1, 1, 0.7],
      }}
      transition={{
        duration: 3.2,
        times: [0, 0.3, 0.8, 1],
        delay,
        repeat: Infinity,
        repeatDelay: CHIP_CYCLE - 3.2,
        ease: [0.2, 0.8, 0.2, 1],
      }}
    >
      <FileText className="h-3.5 w-3.5" style={{ color }} strokeWidth={2.4} />
      <span className="text-[11px] sm:text-xs font-bold tracking-wide" style={{ color }}>
        {label}
      </span>
    </motion.div>
  );
}

/**
 * Conversion narrative animation:
 * 1. A boy on the left holds a physical book; pages fly into the Libros logo.
 * 2. The logo converts them and emits multiple output formats (HTML5, PDF, XML, EPUB...).
 * 3. Loops continuously.
 */
function ConversionScene({ paused }) {
  const CYCLE = 5; // seconds per full cycle
  return (
    <div
      data-testid="conversion-scene"
      aria-hidden="true"
      className="relative mx-auto w-full max-w-4xl h-72 sm:h-80 lg:h-96 mb-8 sm:mb-10"
    >
      {/* Dashed path */}
      <svg
        className="absolute inset-x-0 top-1/2 -translate-y-1/2"
        viewBox="0 0 800 20"
        width="100%"
        height="20"
        preserveAspectRatio="none"
        aria-hidden
      >
        <line
          x1="80"
          y1="10"
          x2="720"
          y2="10"
          stroke="rgba(11,31,58,0.18)"
          strokeWidth="1.5"
          strokeDasharray="4 6"
        />
      </svg>

      {/* Boy holding book — left */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col items-center">
        <motion.div
          animate={paused ? { y: 0 } : { y: [0, -4, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          {/* Head + body silhouette */}
          <div className="relative h-24 w-24 sm:h-32 sm:w-32">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-9 w-9 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-[#0B1F3A] to-[#1F5EFF] flex items-center justify-center shadow-md">
              <User className="h-5 w-5 sm:h-6 sm:w-6 text-white" strokeWidth={2.4} />
            </div>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-14 w-16 sm:h-[72px] sm:w-20 rounded-t-3xl bg-gradient-to-b from-[#1F5EFF] to-[#0B1F3A] shadow-md" />
            {/* Book in hands */}
            <motion.div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 h-7 w-10 sm:h-9 sm:w-13 rounded bg-[#6D28D9] border-2 border-white flex items-center justify-center shadow-lg"
              animate={{ opacity: [1, 1, 0.6, 0.6, 1], scale: [1, 1, 0.9, 0.9, 1] }}
              transition={{
                duration: CYCLE,
                times: [0, 0.1, 0.2, 0.85, 1],
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-white" strokeWidth={2.4} />
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Libros logo — center */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
        <div className="relative">
          {/* Pulsing glow when pages pass through */}
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-full bg-gradient-to-br from-[#1F5EFF]/35 via-[#6D28D9]/30 to-[#00B8A9]/35 blur-2xl"
            animate={paused ? { scale: 1, opacity: 0.6 } : { scale: [1, 1.35, 1, 1.35, 1], opacity: [0.5, 0.95, 0.5, 0.95, 0.5] }}
            transition={{
              duration: CYCLE,
              times: [0, 0.35, 0.5, 0.6, 1],
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.img
            src={LOGO_URL}
            alt=""
            className="relative h-44 w-44 sm:h-56 sm:w-56 lg:h-64 lg:w-64 object-contain mix-blend-multiply"
            animate={paused ? { scale: 1 } : { scale: [1, 1.05, 1, 1.05, 1] }}
            transition={{
              duration: CYCLE,
              times: [0, 0.35, 0.5, 0.6, 1],
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          {/* Sparkles burst when conversion happens */}
          <motion.div
            aria-hidden
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            animate={paused ? { opacity: 0 } : { opacity: [0, 0, 1, 0, 0] }}
            transition={{
              duration: CYCLE,
              times: [0, 0.4, 0.5, 0.6, 1],
              repeat: Infinity,
              ease: "easeOut",
            }}
          >
            <Sparkles className="h-12 w-12 text-[#00B8A9]" strokeWidth={2} />
          </motion.div>
        </div>
      </div>

      {/* Travelling book: from boy → into logo */}
      {!paused && (
      <motion.div
        aria-hidden
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-14 rounded-md bg-[#6D28D9] border-2 border-white flex items-center justify-center shadow-xl"
        animate={{
          left: ["12%", "12%", "50%", "50%", "12%"],
          opacity: [0, 1, 1, 0, 0],
          scale: [0.6, 1, 1, 0.4, 0.6],
          rotate: [0, 0, 360, 360, 0],
        }}
        transition={{
          duration: CYCLE,
          times: [0, 0.15, 0.45, 0.55, 1],
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <BookOpen className="h-5 w-5 text-white" strokeWidth={2.4} />
      </motion.div>
      )}

      {/* Trailing loose pages following the book */}
      {!paused && [0.35, 0.6].map((lag, i) => (
        <motion.div
          key={i}
          aria-hidden
          className="absolute top-1/2 -translate-x-1/2 h-7 w-5 rounded-sm bg-white border border-[#0B1F3A]/20 shadow-md flex flex-col justify-center gap-0.5 px-0.5"
          style={{ marginTop: i === 0 ? -34 : 22 }}
          animate={{
            left: ["10%", "10%", "50%", "50%", "10%"],
            opacity: [0, 0.9, 0.9, 0, 0],
            rotate: [-8 + i * 16, -8 + i * 16, 14 - i * 20, 14 - i * 20, -8 + i * 16],
          }}
          transition={{
            duration: CYCLE,
            times: [0, 0.15 + lag * 0.1, 0.48, 0.56, 1],
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <div className="h-0.5 rounded bg-[#1F5EFF]/50" />
          <div className="h-0.5 w-3/4 rounded bg-[#6D28D9]/40" />
          <div className="h-0.5 w-1/2 rounded bg-[#00B8A9]/40" />
        </motion.div>
      ))}

      {/* Output format chips: from logo → fan out right */}
      {FORMATS.map((f) => (
        <FormatChip key={f.label} {...f} paused={paused} />
      ))}
    </div>
  );
}

export default function Hero() {
  const [paused, setPaused] = useState(false);
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 sm:pt-32 pb-28 sm:pb-36"
    >
      {/* Background blobs */}
      <div className="blob bg-[#1F5EFF]/30 h-[420px] w-[420px] -top-32 -right-32" />
      <div className="blob bg-[#6D28D9]/25 h-[360px] w-[360px] top-32 -left-24" />
      <div className="blob bg-[#00B8A9]/25 h-[300px] w-[300px] bottom-0 left-1/3" />

      <div className="absolute inset-0 grid-bg" aria-hidden />

      {/* WCAG compliance badge — top right */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        data-testid="wcag-badge-hero"
        className="hidden md:inline-flex absolute top-24 right-6 lg:right-10 z-10 items-center gap-1.5 rounded-full border border-[#00B8A9]/40 bg-white/80 backdrop-blur-sm px-3.5 py-1.5 text-[11px] font-semibold text-[#00776E] shadow-[0_8px_24px_-12px_rgba(0,184,169,0.4)]"
      >
        <BadgeCheck className="h-3.5 w-3.5 text-[#00B8A9]" aria-hidden="true" />
        Built with WCAG 2.2 AA best practices
      </motion.div>

      <div className="relative mx-auto max-w-5xl px-5 sm:px-8 text-center">
        <div className="relative">
          <ConversionScene paused={paused} />
          <button
            data-testid="hero-animation-toggle"
            type="button"
            onClick={() => setPaused((p) => !p)}
            aria-pressed={paused}
            className="absolute -bottom-1 right-0 z-10 inline-flex items-center gap-1.5 rounded-full border border-[#0B1F3A]/15 bg-white/90 backdrop-blur-sm px-3.5 py-2 text-[12px] font-semibold text-[#0B1F3A] hover:border-[#1F5EFF]/40 hover:text-[#1F5EFF] transition-colors"
          >
            {paused ? <Play className="h-3.5 w-3.5" aria-hidden="true" /> : <Pause className="h-3.5 w-3.5" aria-hidden="true" />}
            {paused ? "Play animation" : "Pause animation"}
          </button>
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
          <motion.h1
            variants={fadeUp}
            custom={0}
            className="font-display font-bold text-[#0B1F3A] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]"
          >
            Where Content Meets{" "}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-[#1F5EFF] via-[#6D28D9] to-[#00B8A9] bg-clip-text text-transparent">
                Technology
              </span>
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-6 mx-auto max-w-3xl text-base sm:text-lg text-[#475569] leading-relaxed"
          >
            Empowering organizations to create, transform, manage, and deliver exceptional content
            experiences across print and digital platforms. From publishing and accessibility to
            creative services and digital transformation, Libros Consulting delivers integrated
            solutions that help content reach, engage, and inspire audiences worldwide.
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={3}
            className="mt-9 flex flex-wrap items-center justify-center gap-3"
          >
            <a
              data-testid="hero-cta-talk"
              href={CONTACT.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Schedule a Consultation (opens in new tab)"
              className="btn-primary"
            >
              Schedule a Consultation
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <a data-testid="hero-cta-quote" href="#contact" className="btn-ghost">
              Get a Free Quote
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={4}
            data-testid="hero-trust-line"
            className="mt-10"
          >
            <div className="text-[13px] sm:text-[14px] font-bold uppercase tracking-[0.14em] text-[#0B1F3A]">
              Supporting Content-Driven Organizations Worldwide
            </div>
            <div className="mt-2 text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.16em] text-[#475569]">
              Publishing <span aria-hidden className="text-[#1F5EFF]">•</span> Education{" "}
              <span aria-hidden className="text-[#6D28D9]">•</span> Museums{" "}
              <span aria-hidden className="text-[#00B8A9]">•</span> Government{" "}
              <span aria-hidden className="text-[#1F5EFF]">•</span> Healthcare{" "}
              <span aria-hidden className="text-[#6D28D9]">•</span> Professional Associations{" "}
              <span aria-hidden className="text-[#00B8A9]">•</span> Enterprises
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
